import { Component, Input, Output, EventEmitter, OnChanges, HostListener, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SecurityService } from '../security/security.service';
import { JwtHelperService } from '@auth0/angular-jwt';

@Component({
  selector: 'app-darwin-iframe',
  templateUrl: './darwin-iframe.component.html',
  styleUrls: ['./darwin-iframe.component.scss']
})
export class DarwinIframeComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() url: string = '';
  @Output() close = new EventEmitter<void>();

  private safeUrl: SafeResourceUrl = '';
  private tokenAcked = false;
  private ackListener!: (event: MessageEvent) => void;

  private isDragging = false;
  private offsetX = 0;
  private offsetY = 0;
  private readonly STORAGE_KEY = 'darwin-position-size';

  private tokenRefreshTimer: any;
  private readonly TOKEN_REFRESH_INTERVAL_MS = 60000; // 1 minute
  private readonly TOKEN_EXPIRATION_THRESHOLD_MS = 120000; // 2 minutes

  constructor(private sanitizer: DomSanitizer, private el: ElementRef, private _securityService: SecurityService) {}

  ngOnChanges() {
    if (this.url) {
      console.log('[darwin-iframe] Sanitizing URL:', this.url);
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
    }
  }

  ngAfterViewInit() {
    this.restorePositionAndSize();

    this.ackListener = ({ origin, data }: MessageEvent) => {
      console.log('[darwin-iframe] received message event from origin:', origin, 'with data:', data);
      const expectedOrigin = this.getTargetOrigin();
      if (origin !== expectedOrigin || !data || data.type !== 'auth-token-ack') return;

      this.tokenAcked = true;
      console.log('[darwin-iframe] iframe acknowledged auth token');
    };
    window.addEventListener('message', this.ackListener);
    this.startTokenRefreshTimer();
  }

  ngOnDestroy() {
    if (this.ackListener) {
      window.removeEventListener('message', this.ackListener);
    }
    this.stopTokenRefreshTimer();
  }

  closeDarwin() {
    this.savePositionAndSize();
    this.close.emit();
  }

  onIframeLoad() {
    console.log('[darwin-iframe] Iframe loaded, refreshing token before sending to Darwin');
    this._securityService.refreshAndStoreTokens().subscribe(
      () => this.sendTokenToIframe(),
      (error) => {
        console.error('Token refresh failed, falling back to stored token', error);
        this.sendTokenToIframe();
      }
    );
  }

  private getTargetOrigin(): string {
    try {
      return new URL(this.url).origin;
    } catch (e) {
      return '';
    }
  }

  private async sendTokenToIframe(retries = 10) {
    const token = localStorage.getItem('ask_canteen_access_token');
    if (!token) {
      console.error('[darwin-iframe] no ask_canteen_access_token in localStorage');
      return;
    }

    const iframe: HTMLIFrameElement = this.el.nativeElement.querySelector('iframe');
    if (!iframe || !iframe.contentWindow) {
      console.error('[darwin-iframe] iframe.contentWindow is null — cannot postMessage');
      return;
    }

    const targetOrigin = this.getTargetOrigin();
    if (!targetOrigin) {
      console.error('[darwin-iframe] could not derive target origin from url:', this.url);
      return;
    }

    const MAX_ATTEMPTS = 20;
    const INTERVAL_MS = 500;
    for (let i = 0; i < MAX_ATTEMPTS && !this.tokenAcked; i++) {
      try {
        iframe.contentWindow.postMessage({ type: 'auth-token', token }, targetOrigin);
      } catch (err) {
        console.error('[darwin-iframe] postMessage attempt ' + (i + 1) + ' threw:', err);
      }
      if (i === 0) {
        console.log('[darwin-iframe] sent first auth-token postMessage to ' + targetOrigin);
      }
      await new Promise((r) => setTimeout(r, INTERVAL_MS));
    }
    if (!this.tokenAcked) {
      console.log('[darwin-iframe] stopped resending auth token after ' + MAX_ATTEMPTS + ' attempts (no ack received)');
    }    
  }

  startDrag(event: MouseEvent) {
    event.preventDefault();
    this.isDragging = true;
    const parentElement = this.el.nativeElement.parentElement;
    const rect = parentElement.getBoundingClientRect();
    this.offsetX = event.clientX - rect.left;
    this.offsetY = event.clientY - rect.top;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    const parentElement = this.el.nativeElement.parentElement;
    const rect = parentElement.getBoundingClientRect();
    let left = event.clientX - this.offsetX;
    let top = event.clientY - this.offsetY;
    left = Math.max(0, Math.min(left, window.innerWidth - rect.width));
    top = Math.max(0, Math.min(top, window.innerHeight - rect.height));
    parentElement.style.left = `${left}px`;
    parentElement.style.top = `${top}px`;
    parentElement.style.right = 'auto';
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    if (this.isDragging) {
      this.isDragging = false;
      this.savePositionAndSize();
    }
  }

  private savePositionAndSize() {
    const parentElement = this.el.nativeElement.parentElement;
    const data = {
      left: parentElement.style.left,
      top: parentElement.style.top,
      width: parentElement.style.width,
      height: parentElement.style.height
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
  }

  private restorePositionAndSize() {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      const parentElement = this.el.nativeElement.parentElement;
      if (data.width) parentElement.style.width = data.width;
      if (data.height) parentElement.style.height = data.height;
      if (data.left || data.top) {
        parentElement.style.right = 'auto';
        const rect = parentElement.getBoundingClientRect();
        const left = Math.max(0, Math.min(parseInt(data.left, 10) || 0, window.innerWidth - rect.width));
        const top = Math.max(0, Math.min(parseInt(data.top, 10) || 0, window.innerHeight - rect.height));
        parentElement.style.left = `${left}px`;
        parentElement.style.top = `${top}px`;
      }
    }
  }

  private startTokenRefreshTimer() {
    this.tokenRefreshTimer = setInterval(() => {
      this.checkAndRefreshTokenIfNeeded();
    }, this.TOKEN_REFRESH_INTERVAL_MS);
  }

  private stopTokenRefreshTimer() {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  private checkAndRefreshTokenIfNeeded() {
    const token = localStorage.getItem('ask_canteen_access_token');
    if (!token) {
      return;
    }

    const helper = new JwtHelperService();
    const decodedToken = helper.decodeToken(token);
    if (!decodedToken || !decodedToken.exp) {
      return;
    }

    const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;

    if (timeUntilExpiration < this.TOKEN_EXPIRATION_THRESHOLD_MS) {
      console.log('[darwin-iframe] token will expire in less than 2 minutes, refreshing');
      this._securityService.refreshAndStoreTokens().subscribe(
        () => {
          console.log('[darwin-iframe] token refreshed successfully');
          this.sendTokenToIframe();
        },
        (error) => {
          console.error('[darwin-iframe] token refresh failed during timer check', error);
        }
      );
    }
  }
}